import { apiService } from '../services/api';
import { FIRST_TIER_TAB_IDS } from '../components/Dashboard/tabs/registry';
import { persistSelectedSerial } from './dashboardPersistence';
import { isPushingState, parseDashboardUrl, replaceUrlState } from './dashboardUrlSync';
import { DashboardActions, DashboardGet, DashboardSet } from './dashboardStore.types';

type EffectsActions = Pick<
  DashboardActions,
  | 'initializeFromUrl'
  | 'handlePopState'
  | 'handleSliderEffects'
  | 'updateSerialNumbers'
  | 'loadFlightTypesIfNeeded'
>;

/**
 * URL/history + slider effects and the serial-number/flight-type loaders.
 * Split out of dashboardStore.ts (STD-014) as a slice factory taking the
 * store's (set, get); behavior is unchanged.
 */
export const createEffectsActions = (set: DashboardSet, get: DashboardGet): EffectsActions => ({
  updateSerialNumbers: async (flightTypeFilter?: string) => {
    const { ongoingRequests } = get();
    const requestKey = `serial-numbers-${flightTypeFilter || 'all'}`;

    if (ongoingRequests.has(requestKey)) {
      console.log('🔄 Preventing duplicate serial numbers request');
      return;
    }

    ongoingRequests.add(requestKey);

    try {
      // Fetch all drones and stats in parallel
      const [allDronesData, stats] = await Promise.all([
        apiService.getAllDronesSimple(),
        apiService.getStats(undefined, undefined, flightTypeFilter),
      ]);

      const serialNumbers = allDronesData.map((d) => d.serial_number);

      set(
        (state) => ({
          data: {
            ...state.data,
            serialNumbers,
            stats,
          },
        }),
        false,
        'updateSerialNumbers'
      );
    } catch (error) {
      console.error('📊 Dashboard Store: Failed to update serial numbers:', error);
      set(
        {
          error: 'Failed to update data',
        },
        false,
        'updateSerialNumbers-error'
      );
    } finally {
      ongoingRequests.delete(requestKey);
    }
  },

  loadFlightTypesIfNeeded: async () => {
    const { ongoingRequests, data } = get();
    const requestKey = 'load-flight-types';

    // If we already have flight types, don't load again
    if (data.flightTypes.length > 0) {
      return;
    }

    // Skip if already loading
    if (ongoingRequests.has(requestKey)) {
      return;
    }

    ongoingRequests.add(requestKey);

    try {
      const allFlightTypes = await apiService.getFlightTypes();

      set(
        (state) => ({
          data: {
            ...state.data,
            flightTypes: allFlightTypes,
          },
        }),
        false,
        'loadFlightTypesIfNeeded'
      );
    } catch (error) {
      console.error('📊 Dashboard Store: Failed to load flight types:', error);
      set(
        {
          error: 'Failed to load flight types',
        },
        false,
        'loadFlightTypesIfNeeded-error'
      );
    } finally {
      ongoingRequests.delete(requestKey);
    }
  },

  // Effects actions
  //
  // Both of these read the url through the ONE parser, `parseDashboardUrl`.
  // They used to carry a copy each, which is how they came to disagree — and a
  // second copy is what a folder-aware url would have made expensive to keep
  // honest. Neither reads react-router: these are raw history writes, so
  // react-router's location is stale between navigations (see
  // `dashboardUrlSync`).
  initializeFromUrl: () => {
    const parsed = parseDashboardUrl(window.location.pathname, window.location.search);

    // Set state directly (bypass syncUrlState to avoid pushing on mount)
    if (parsed.serial) {
      persistSelectedSerial(parsed.serial);
      set({ selectedSerial: parsed.serial }, false, 'initializeFromUrl-serial');
    }
    set({ activeTab: parsed.tab }, false, 'initializeFromUrl-tab');
    if (parsed.tab === 'files') {
      set(
        { filesFolderId: parsed.folderId, filesFolderInvalid: parsed.folderInvalid },
        false,
        'initializeFromUrl-folder'
      );
    }

    // Replace the current entry with the canonical url: aliases mapped,
    // `?tab=files` rewritten to `/files`, and a `?tab=` that contradicts a
    // `/files/...` path dropped. The serial written is the one the URL carried,
    // never the persisted one — otherwise a cold load on `?tab=profile` would
    // silently gain a `&serial=` the user did not ask for.
    const folderId = parsed.tab === 'files' ? parsed.folderId : get().filesFolderId;
    replaceUrlState(parsed.tab, parsed.serial, folderId);
  },

  handlePopState: () => {
    if (isPushingState()) return;

    const parsed = parseDashboardUrl(window.location.pathname, window.location.search);
    const tab = parsed.tab;

    // On first-tier tabs the URL won't have a serial, but we keep the
    // in-memory selectedSerial so the UI doesn't reload. Only update
    // serial state when navigating to a drone-specific tab.
    if (!FIRST_TIER_TAB_IDS.has(tab)) {
      const prevSerial = get().selectedSerial;
      if (parsed.serial !== prevSerial) {
        persistSelectedSerial(parsed.serial);
        set({ selectedSerial: parsed.serial }, false, 'popstate-serial');
        if (!parsed.serial) {
          get().resetDroneData();
        }
      }
    }
    if (tab !== get().activeTab) {
      set({ activeTab: tab }, false, 'popstate-tab');
    }
    // Only a Files url says anything about the folder. Leaving the tab must not
    // clear it — the folder is remembered so returning restores it, and Back out
    // of the tab is exactly that transition.
    if (tab === 'files') {
      set(
        { filesFolderId: parsed.folderId, filesFolderInvalid: parsed.folderInvalid },
        false,
        'popstate-folder'
      );
    }
  },

  handleSliderEffects: () => {
    const { selectedSerial, sliderValues, prevSliderValues, data, loadDroneData } = get();

    if (!selectedSerial || !data.dateRange) {
      return;
    }

    // Check if slider values have changed
    const sliderChanged =
      sliderValues[0] !== prevSliderValues[0] || sliderValues[1] !== prevSliderValues[1];

    if (sliderChanged) {
      // Debounced function to load drone data
      const timeoutId = setTimeout(() => {
        const getDateFromSliderValue = (value: number): string | undefined => {
          if (!data.dateRange?.flight_dates || data.dateRange.flight_dates.length === 0) {
            return undefined;
          }
          const flightDates = data.dateRange.flight_dates;
          const index = Math.floor((value / 100) * (flightDates.length - 1));
          return flightDates[index];
        };

        const startDate = getDateFromSliderValue(sliderValues[0]);
        const endDate = getDateFromSliderValue(sliderValues[1]);

        loadDroneData(selectedSerial, startDate, endDate);
        set({ prevSliderValues: sliderValues }, false, 'handleSliderEffects');
      }, 500);

      // Store timeout to allow cleanup
      return () => clearTimeout(timeoutId);
    }
  },
});
